import type { TFunction } from "i18next";

/**
 * Map a structured error kind (from the backend errorClassifier) directly to a friendly
 * sentence. Use this when you have the kind already (e.g. account.lastFailureKind or
 * jobRun.errorKind); for raw error strings use humanizeError instead.
 */
export function humanizeErrorKind(
  kind: string | undefined | null,
  t: TFunction
): string | null {
  if (!kind) return null;
  switch (kind) {
    case "transient_network":
      return t("errors.kindTransientNetwork");
    case "auth_required":
      return t("errors.kindAuthRequired");
    case "challenge":
      return t("errors.kindChallenge");
    case "rate_limited":
      return t("errors.kindRateLimited");
    case "bad_credentials":
      return t("errors.kindBadCredentials");
    case "unknown":
      return t("errors.kindUnknown");
    case "network_blip":
      return t("errors.kindTransientNetwork");
    case "browser_blip":
      return t("errors.kindBrowserBlip");
    case "session_expired":
      return t("errors.kindSessionExpired");
    case "challenge_required":
      return t("errors.kindChallenge");
    default:
      return null;
  }
}

/**
 * Translates raw automation/Playwright error strings into a single short, user-friendly
 * sentence in the user's locale. The mapping is intentionally pattern-based (case
 * insensitive substring match) so we keep working even when Playwright tweaks the wording
 * or Instagram changes its UI - we just add another rule.
 *
 * If nothing matches we return null so callers can fall back to the raw text behind a
 * "Show details" toggle, which is still useful for the user when reporting a bug.
 */
export function humanizeError(raw: string | undefined | null, t: TFunction): string | null {
  if (!raw) return null;
  const s = raw.toLowerCase();

  if (
    s.includes("target page, context or browser has been closed") ||
    s.includes("target closed") ||
    s.includes("browser has been closed")
  ) {
    return t("errors.sessionClosed");
  }
  if (s.includes("worker restarted before the job could finish")) {
    return t("errors.workerRestarted");
  }
  if (
    s.includes("opening in existing browser session") ||
    s.includes("profileinuse")
  ) {
    return t("errors.sessionConflict");
  }
  if (s.includes("net::err_aborted") || s.includes("net::err_internet_disconnected")) {
    return t("errors.networkAborted");
  }
  if (s.includes("net::err_") || s.includes("err_connection")) {
    return t("errors.networkError");
  }
  if (s.includes("login failed")) {
    return t("errors.loginFailed");
  }
  if (
    s.includes("locator.click: timeout") ||
    s.includes("locator.waitfor") ||
    s.includes("waiting for locator")
  ) {
    return t("errors.elementMissing");
  }
  if (s.includes("page.goto") && s.includes("timeout")) {
    return t("errors.pageLoadTimeout");
  }
  if (s.includes("could not open follow") || s.includes("openfollowlist")) {
    return t("errors.followerListUnavailable");
  }
  if (s.includes("soft_block_scraping_warning") || s.includes("scraping_warning")) {
    return t("errors.softBlockScrapingWarning");
  }
  if (s.includes("no_active_stories")) {
    return t("errors.noActiveStories");
  }
  if (s.includes("no_posts")) {
    return t("errors.noPosts");
  }
  if (s.includes("no_on_follow_template")) {
    return t("errors.noOnFollowTemplate");
  }
  if (s.includes("no_campaign_with_dm_on_follow")) {
    return t("errors.noCampaignWithDmOnFollow");
  }
  if (s.includes("snapshot_warming_up")) {
    return t("errors.snapshotWarmingUp");
  }
  if (s.includes("snapshot_mismatch_too_many_new")) {
    return t("errors.snapshotMismatch");
  }
  if (s.includes("follow_button_not_found")) {
    return t("errors.followButtonMissing");
  }
  if (s.includes("unfollow_button_not_found")) {
    return t("errors.unfollowButtonMissing");
  }
  if (s.includes("kept_mutual")) {
    return t("errors.keptMutual");
  }
  if (s.includes("already_follower")) {
    return t("errors.alreadyFollower");
  }
  if (s.includes("cap reached") || s.includes("daily cap") || s === "cap_reached") {
    return t("errors.dailyCapReached");
  }
  if (s.includes("hourly cap")) {
    return t("errors.hourlyCapReached");
  }
  if (s.includes("rate limit")) {
    return t("errors.rateLimited");
  }
  // Batch-level skip codes the workers write to `payload` when they exit
  // without performing any action — keep them in sync with the worker code
  // (viewStories, likePosts, follow, unfollow, comment).
  if (s === "no_targets") {
    return t("errors.noTargets");
  }
  if (s === "no_templates") {
    return t("errors.noTemplates");
  }
  if (s === "no_due_followings") {
    return t("errors.noDueFollowings");
  }
  // Like-post fallback outcomes: the user sees these in their Activity
  // Log when viewStories tried to like a recent post but couldn't.
  if (s === "all_already_liked" || s === "no_fresh_posts") {
    return t("errors.allAlreadyLiked");
  }
  if (s === "profile_lookup_failed") {
    return t("errors.profileLookupFailed");
  }
  if (
    s === "like_endpoint_rejected" ||
    s === "fallback_failed" ||
    s === "fallback_error" ||
    s === "feed_api_failed" ||
    s === "api_empty_verify_dom"
  ) {
    return t("errors.likeFallbackFailed");
  }

  return null;
}

/**
 * Some interactions store a structured machine code in the `payload` field instead of a
 * free-form message (e.g. "kept_mutual" on a successful unfollow). When that's the case,
 * humanize it; otherwise return the payload as-is so the user sees their DM/comment text.
 */
export function humanizePayload(raw: string | undefined | null, t: TFunction): string | null {
  if (!raw) return null;
  const s = raw.trim();
  // Treat short snake_case codes as machine codes; anything else is real content.
  if (/^[a-z][a-z0-9_]{0,32}$/.test(s)) {
    const mapped = humanizeError(s, t);
    if (mapped) return mapped;
  }
  return raw;
}
